import * as LucideIcons from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Clock, Wrench, ArrowRight, Sparkles } from "lucide-react";
import ConfidenceBadge from "@/components/ConfidenceBadge";

// Phase 7 (v1 launch cleanup): display-label translation ONLY — the
// backend's auto-generated label (from signal_id, e.g. "ai.entity_consistency"
// -> "Entity consistency") is technically accurate but reads as raw jargon
// to a contractor. This overrides ONLY the specific labels flagged as
// unclear; every other signal's backend-provided label is used unchanged.
// signal_id itself, evidence, and scoring are untouched — this is a purely
// cosmetic lookup keyed off the signal_id already present on each evidence
// row.
const EVIDENCE_LABEL_OVERRIDES = {
  "ai.entity_consistency": "Business info matches across Google & website",
  "technical.lcp_ms": "Page load speed (LCP)",
  "technical.cls": "Visual stability while loading (CLS)",
  "technical.inp_ms": "Responsiveness to clicks/taps (INP)",
  "local.nap_consistent": "Name, address & phone consistency",
  "local.citation_coverage": "Business directory listings",
};

export default function CategoryDetailDialog({
  open,
  onOpenChange,
  category,
  narration,
}) {
  if (!category) return null;
  const Icon = LucideIcons[category.icon] || LucideIcons.Shield;
  const recLookup = new Map(
    (narration?.recommendations || []).map((r) => [r.finding_id, r]),
  );
  // Reuses the backend's existing roadmap_only evidence flag — no new
  // evidence status, no scoring change. A signal is roadmap_only when it
  // is structurally unmeasurable under the current build (no live
  // provider exists at all), as opposed to merely unresolved this run.
  const evidenceList = category.evidence || [];
  const partialScope = evidenceList.some((e) => e.roadmap_only);
  const label = (e) => EVIDENCE_LABEL_OVERRIDES[e.signal_id] || e.label;
  const verifiedLabels = evidenceList
    .filter((e) => !e.roadmap_only && (e.status === "verified" || e.status === "calculated"))
    .map(label);
  const unavailableRoadmapLabels = evidenceList.filter((e) => e.roadmap_only).map(label);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-[#1A1F24] border border-[#333C47] text-white rounded-none p-0"
        data-testid="category-detail-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#333C47]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 border border-[#333C47] bg-[#0F1115]">
                <Icon className="h-5 w-5 text-[#6CC24A]" />
              </div>
              <div>
                <div className="pp-label mb-1 flex items-center gap-2">
                  Trust Category
                  <ConfidenceBadge
                    confidence={category.confidence || "calculated"}
                    source={category.source}
                    partialScope={partialScope}
                  />
                </div>
                <DialogTitle className="font-display text-2xl text-white leading-tight">
                  {category.name}
                </DialogTitle>
                <DialogDescription className="text-sm text-[#C7CBD1] mt-2 max-w-md">
                  {category.summary}
                </DialogDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="pp-label mb-1">Score</div>
              <div className="metric text-4xl font-bold text-white">
                {category.score != null ? category.score : "—"}
                <span className="text-lg text-[#C7CBD1]">/100</span>
              </div>
              {typeof category.confidence_pct === "number" && (
                <div
                  className="mt-2 text-[10px] uppercase tracking-wider font-semibold"
                  style={{
                    color:
                      category.confidence === "verified"
                        ? "#6CC24A"
                        : category.confidence === "unavailable"
                          ? "#EAB308"
                          : "#C7CBD1",
                  }}
                  data-testid={`category-confidence-pct-${category.id}`}
                  title="Share of this category's measurable evidence that was actually collected this run. A category can be Verified while this is under 100% — Verified means every REQUIRED signal was confirmed, not that every optional signal resolved."
                >
                  {category.confidence_pct}% evidence confidence
                </div>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
          {/* Measurement scope — only rendered when this category has at
              least one structurally unmeasurable (roadmap_only) signal.
              Reuses existing evidence data; no scoring/evidence change. */}
          {partialScope && (
            <div
              data-testid={`scope-notice-${category.id}`}
              className="pp-card p-4 text-sm border"
              style={{ borderColor: "#EAB30855" }}
            >
              <div className="pp-label mb-2" style={{ color: "#EAB308" }}>
                Measurement Scope
              </div>
              <p className="text-xs text-[#C7CBD1] leading-relaxed mb-3">
                This score is based only on the signals we can currently
                measure. It is not a claim that the complete {category.name}{" "}
                picture was verified.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6CC24A] mb-1.5">
                    Verified
                  </div>
                  <ul className="space-y-1 text-xs text-white">
                    {verifiedLabels.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#EAB308] mb-1.5">
                    Unavailable
                  </div>
                  <ul className="space-y-1 text-xs text-[#C7CBD1]">
                    {unavailableRoadmapLabels.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          {/* Points breakdown — baseline + gains − losses = final */}
          {category.breakdown && (
            <div data-testid={`breakdown-${category.id}`}>
              <div className="pp-label mb-2">Why this score</div>
              <div className="pp-card p-4 text-sm">
                <div className="flex items-baseline justify-between py-1.5 border-b border-[#333C47]">
                  <div className="text-[#C7CBD1]">
                    {category.breakdown.baseline_label || "Baseline"}
                  </div>
                  <div className="metric text-white font-semibold">
                    {category.breakdown.baseline}
                  </div>
                </div>
                {(category.breakdown.gains || []).map((g, i) => (
                  <div
                    key={`g-${i}`}
                    className="flex items-baseline justify-between py-1.5 border-b border-[#333C47] last:border-0"
                  >
                    <div className="text-white truncate mr-3">
                      <span className="text-[#6CC24A] mr-2">+</span>
                      {g.label}
                    </div>
                    <div className="metric text-[#6CC24A] font-semibold shrink-0">
                      +{g.points}
                    </div>
                  </div>
                ))}
                {(category.breakdown.losses || []).map((l, i) => (
                  <div
                    key={`l-${i}`}
                    className="flex items-baseline justify-between py-1.5 border-b border-[#333C47] last:border-0"
                  >
                    <div className="text-white truncate mr-3">
                      <span className="text-[#EF4444] mr-2">−</span>
                      {l.label}
                    </div>
                    <div className="metric text-[#EF4444] font-semibold shrink-0">
                      −{l.points}
                    </div>
                  </div>
                ))}
                <div className="flex items-baseline justify-between pt-2 mt-1 border-t border-[#6CC24A]/40">
                  <div className="pp-label">Final score</div>
                  <div className="metric text-white font-bold text-lg">
                    {category.breakdown.final}
                    <span className="text-xs text-[#C7CBD1]"> /100</span>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-[#C7CBD1] mt-2 leading-relaxed">
                The baseline is a fixed starting point in Pulse Point's scoring
                model, not an industry average. Every gain and every leak
                applied on top of it is shown above — nothing is hidden.
              </div>
            </div>
          )}

          {/* Evidence trail */}
          {category.evidence?.length > 0 && (
            <div data-testid={`evidence-${category.id}`}>
              <div className="pp-label mb-2">Evidence used</div>
              <div className="pp-card divide-y divide-[#333C47]">
                {category.evidence.map((e, i) => {
                  const color =
                    e.status === "verified"
                      ? "#6CC24A"
                      : e.status === "unavailable"
                        ? "#EAB308"
                        : "#C7CBD1";
                  return (
                    <div
                      key={i}
                      className="flex items-start justify-between gap-3 p-3 text-sm"
                      data-testid={`evidence-item-${category.id}-${i}`}
                    >
                      <div className="min-w-0">
                        <div className="text-white">
                          {EVIDENCE_LABEL_OVERRIDES[e.signal_id] || e.label}
                        </div>
                        <div className="text-[11px] text-[#C7CBD1] mt-0.5">
                          {e.source}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-[#C7CBD1] text-xs">
                          {typeof e.value === "number"
                            ? e.value
                            : String(e.value)}
                        </div>
                        <span
                          className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border"
                          style={{
                            color,
                            borderColor: `${color}55`,
                            background: `${color}0F`,
                          }}
                        >
                          {e.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {category.strengths?.length > 0 && (
            <div>
              <div className="pp-label mb-2 text-[#6CC24A]">
                Already working
              </div>
              <ul className="space-y-2">
                {category.strengths.map((s, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-sm text-white"
                    data-testid={`strength-${category.id}-${i}`}
                  >
                    <Sparkles className="h-4 w-4 text-[#6CC24A] mt-0.5" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="pp-label mb-3">
              Issues in this category ({category.findings?.length || 0})
            </div>
            <div className="space-y-4">
              {(category.findings || []).length === 0 && (
                <div className="text-sm text-[#C7CBD1]">
                  Nothing critical detected. Keep the momentum.
                </div>
              )}
              {(category.findings || []).map((f, i) => {
                const rec = recLookup.get(f.id);
                return (
                  <div
                    key={f.id}
                    className="pp-card p-4"
                    data-testid={`category-finding-${i}`}
                  >
                    <div className="font-display text-white font-semibold text-base leading-snug">
                      {rec?.headline || f.title}
                    </div>
                    {(rec?.why_it_matters || f.why) && (
                      <div className="mt-2 text-sm text-[#C7CBD1] leading-relaxed">
                        <span className="text-white font-medium">Why: </span>
                        {rec?.why_it_matters || f.why}
                      </div>
                    )}
                    {(rec?.how_to_fix || f.fix) && (
                      <div className="mt-1.5 text-sm text-[#C7CBD1] leading-relaxed">
                        <span className="text-[#6CC24A] font-medium">Fix: </span>
                        {rec?.how_to_fix || f.fix}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {f.impact != null && (
                        <span className="pp-badge-green">+{f.impact} trust</span>
                      )}
                      <span className="pp-badge-neutral">
                        <Clock className="h-3 w-3" /> {f.time}
                      </span>
                      <span className="pp-badge-neutral">
                        <Wrench className="h-3 w-3" /> {f.difficulty}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
